import { datadogRum } from '@datadog/browser-rum'
import { ApolloError } from '@apollo/client'
import {
  FIRST_PAGE_NUMBER,
  PRODUCTS_PAGE_SIZE,
} from '../../consts/defaultConfigValues/defaultConstants'
import { useLazyGetSession } from '../../graphQL/queries/getSession'
import { useLazyGetCart } from '../../graphQL/queries/getCart'
import { useLazyGetCatalog } from '../../graphQL/queries/getCatalog'
import { ErrorMessageTranslation, LoadersType } from '../../interfaces'
import { setBanners } from '../../redux/slices/banners/banners'
import { setCategoryList } from '../../redux/slices/catalog'
import {
  addLoader,
  manageErrors,
  applyConfigForSession,
  removeLoader,
} from '../../redux/slices/config'
import castBanners from '../../utils/castBanners'
import {
  castICatalogItemToICategoryArray,
  filterCategoriesForMenu,
} from '../../utils/categoryUtils'
import { generateDataDogContext } from '../../utils/dataDog'
import { onCartResponse } from '../../utils/onCartResponse'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import { useAdvancedFilters } from '../filters/useFilters'
import {
  ISession,
  SessionDataArgs,
} from '../../graphQL/queries/getSession/types'
import { ICatalog, ICatalogItem } from '../../graphQL/queries/getCatalog/types'
import { setMessage, setShow } from '../../redux/slices/globalPromotions'
import { getMultipleTotalPromotionsLabel } from '../../utils/promotions/getTotalPromotionsLabel'
import { CartProductResponse, IPromotion } from '../../graphQL/commons'
import { castICatalogItems } from '../../utils/castICatalogItems'
import { writeSessionData } from '../../utils/writeSessionData'
import getUrlParams from '../../utils/getUrlParams'

const { language } = getUrlParams(window.location.pathname)

export const useInitialCatalogData = () => {
  const dispatch = useAppDispatch()
  const state = useAppSelector((reduxState) => reduxState)
  const store = useAppStore()
  const getAdvancedFiltersValues = useAdvancedFilters()

  const globalErrorConfig = {
    errorMessageTranslation: ErrorMessageTranslation.InitialData,
    showErrorTitle: true,
    globalError: true,
  }

  /**
   * The config as the store holds it now, not as `state` held it when this
   * closure was built (COM-974).
   *
   * **`config` moves during this hook's own round trip, and the value that
   * moves is one every price string below is baked from.** `setSessionData`
   * recomputes `config.options.hidePricesActive` from the session's customer
   * custom fields (`redux/slices/config/config.ts`), and `onSessionCompleted`
   * dispatches it during this hook's own round trip. At boot
   * `fetchConfig.fulfilled` had resolved that flag against `sessionData ===
   * undefined`, so the snapshot always says `false`. Reading the config from
   * `state` therefore baked visible money into the catalogue for a hide-prices
   * shopper: `castICatalogItems` -> `sanitizeExtraPackages` /
   * `sanitizeBundleExtra` -> `formatPrice({ hidePrices })`, and the result is
   * *stored* in the catalog slice rather than re-derived per render, so no
   * later render corrects it.
   *
   * Until COM-974 a second prefetch pass covered this by accident: the
   * `config` identity change re-ran `containers/Config.tsx`'s session effect,
   * and the second pass re-baked the same catalogue against the flipped flag.
   * Gating the prefetch removed that pass and exposed the stale read, which is
   * the real bug — the second round trip was never the fix.
   *
   * **Since COM-1709 the whole config, not only `hidePricesActive`, can differ
   * between the two reads.** Writers mid-prefetch include
   * `fetchConfig.fulfilled` (only on a slug change), `setSessionData` (only
   * `hidePricesActive`), and two that replace the whole config before the
   * bake below: `refetchConfigForCustomer.fulfilled` and `restoreBootConfig`
   * (grep for others rather than trusting this list).
   *
   * The rest of `state` is deliberately still the snapshot. `onCartResponse`
   * reads six other slices from it and they are stale in the same way; making
   * *those* fresh is a separate, wider behaviour change (`filtersSlice.results`
   * can be written mid-round-trip by `getAdvancedFiltersValues`) and is not
   * what this ticket measured.
   */
  const currentConfig = () => store.getState().defaultSlice.config

  const stateWithCurrentConfig = () => ({
    ...state,
    defaultSlice: { ...state.defaultSlice, config: currentConfig() },
  })

  const onCompleted = (
    sessionData: SessionDataArgs,
    catalogData: ICatalogItem[],
    cartData: CartProductResponse,
    totalPromotions: IPromotion[],
  ) => {
    const categories = castICatalogItems(catalogData, currentConfig())

    onCartResponse({
      data: cartData,
      categories,
      sessionData,
      dispatch,
      state: stateWithCurrentConfig(),
    })
    if (totalPromotions.length > 0) {
      dispatch(
        setMessage(
          getMultipleTotalPromotionsLabel(totalPromotions, currentConfig()),
        ),
      )
      dispatch(setShow(true))
    }
  }

  const onError = (error: ApolloError) => {
    dispatch(manageErrors(globalErrorConfig))
    const context = generateDataDogContext({
      title: 'Get initial catalog data got an error',
      extraInfo: { function: 'useInitialCatalogData > onError' },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  const onSessionCompleted = ({ session }: ISession) => {
    writeSessionData({
      session,
      dispatch,
      localization: currentConfig()?.options?.localization,
    })

    dispatch(setBanners(castBanners(session.banners || [])))
    if (
      session.configuration?.filters?.promotions.active ||
      session.configuration?.filters?.categories.active ||
      session.configuration?.filters?.brands.active
    ) {
      getAdvancedFiltersValues()
    }
  }

  const onCatalogCompleted = (catalogResponse: ICatalog) => {
    const filteredCategories = filterCategoriesForMenu(catalogResponse.catalog)
    const sanitizedCategories =
      castICatalogItemToICategoryArray(filteredCategories)

    dispatch(setCategoryList(sanitizedCategories))
  }

  const [getSession] = useLazyGetSession({
    onCompleted: onSessionCompleted,
    onError,
  })

  const [getCart] = useLazyGetCart({
    onError,
  })

  const [getCatalog] = useLazyGetCatalog({
    onCompleted: onCatalogCompleted,
    onError,
    errorPolicy: 'ignore',
  })

  /**
   * Whether this attempt populated the catalogue, so the caller can decide
   * whether to let a later one through (COM-974).
   *
   * **A resolved promise is not a successful prefetch here, and that gap is
   * what the boolean closes.** `getCatalog` carries `errorPolicy: 'ignore'`,
   * under which Apollo deletes `result.errors` and never calls `onError` — so
   * a catalog resolver that fails leaves `catalogData` absent, the conjunction
   * below false, `onCompleted` skipped, and *nothing* dispatched: no
   * `globalError`, no alert, no Datadog error. The shopper gets a fully
   * rendered, empty storefront.
   *
   * Before this ticket that state was recoverable by accident, because any
   * boundary crossing re-fired the prefetch. `containers/Config.tsx` now gates
   * on the `(slug, sessionId)` pair, and the gate is written before the call —
   * so without this signal a silent failure would be recorded as done and no
   * in-app action could ever retry it. Only a full page reload would, and a
   * webview may offer none.
   *
   * `false` therefore means "do not treat this pair as fetched", which covers
   * the empty session id, the thrown exception and the incomplete response
   * alike. `true` means `onCompleted` ran.
   */
  const fetchInitialDataAction = async (
    sessionId: string,
    storeName: string,
  ): Promise<boolean> => {
    try {
      if (!sessionId) {
        dispatch(manageErrors(globalErrorConfig))
        return false
      }
      dispatch(addLoader(LoadersType.INITIAL_DATA))

      /*
       * The catalogue waits for the session and the config it calls for
       * (COM-1709): a customer bundle can change how products are requested,
       * `paginationSize` included. The cart does not depend on the config, so
       * it goes out with the session. `applyConfigForSession` never rejects
       * and is bounded by `CUSTOMER_CONFIG_WAIT_MS`.
       */
      const cartRequest = getCart({
        variables: {
          cartStorefrontName: storeName,
          cartSessionUid: sessionId,
        },
      })

      // Awaited below; this only stops a rejection from going unhandled
      // while the session and config are still pending.
      cartRequest.catch(() => {})

      const { data: sessionData } = await getSession({
        variables: {
          storefrontName: storeName,
          sessionUid: sessionId,
        },
      })

      if (sessionData?.session) {
        await dispatch(
          applyConfigForSession({
            session: sessionData.session,
            slug: storeName,
            language,
            sessionId,
          }),
        )
      }

      return await Promise.all([
        getCatalog({
          variables: {
            showChildrenCategories: true,
            catalogStorefrontName: storeName,
            catalogSessionId: sessionId,
            totalPromotionsSessionId: sessionId,
            pagination: {
              pageNumber: FIRST_PAGE_NUMBER,
              pageSize:
                currentConfig()?.options?.paginationSize || PRODUCTS_PAGE_SIZE,
            },
            groupCategories: true,
          },
        }),
        cartRequest,
      ]).then((values) => {
        const [{ data: catalogData }, { data: cartData }] = values

        if (sessionData?.session && catalogData?.catalog && cartData?.cart) {
          onCompleted(
            sessionData.session,
            catalogData.catalog,
            cartData.cart,
            catalogData.getTotalPromotions,
          )
          return true
        }

        /*
         * The one branch that used to be silent (COM-974).
         *
         * What is reported is the *conjunction* — an attempt that settled
         * without populating the catalogue — and not any one query's failure.
         *
         * **Which of the three reasons is actually live, so a breakdown in
         * Datadog is not read as three measured populations.** `catalog` is
         * the one this ticket found and the only one that can be silent:
         * `getCatalog` carries `errorPolicy: 'ignore'`, under which Apollo
         * deletes `result.errors`, calls no `onError`, and resolves with no
         * `data` — measured, in
         * `tests/initialCatalogDataFailureSignal.test.tsx`. `session` and
         * `cart` reach here too, but only alongside their own `onError`, which
         * has already raised the error screen; this report adds the fact that
         * the catalogue is consequently empty, which `onError` does not say.
         * A null payload with no error on either of those is possible per the
         * schema and has not been observed — defensive, and say so rather than
         * letting a zero read as good news.
         */
        const missing: string[] = []
        if (!sessionData?.session) {
          missing.push('session')
        }
        if (!catalogData?.catalog) {
          missing.push('catalog')
        }
        if (!cartData?.cart) {
          missing.push('cart')
        }
        const context = generateDataDogContext({
          title: 'initial catalog data incomplete',
          extraInfo: {
            function: 'useInitialCatalogData',
            missing,
            storefrontName: storeName,
            sessionUid: sessionId,
          },
        })
        // Load-bearing rather than boilerplate: with manual views an error
        // raised while no view is active is dropped at assembly.
        datadogRum.startView(context.viewName)
        datadogRum.addError(
          new Error(
            `initial catalog data settled without ${missing.join(
              ', ',
            )}; the catalogue is left empty and the shopper is shown no error`,
          ),
          context,
        )
        return false
      })
    } catch (exception) {
      const context = generateDataDogContext({
        title: 'could not load initial catalog data',
        extraInfo: { function: 'useInitialCatalogData' },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(exception, context)
      return false
    } finally {
      dispatch(removeLoader(LoadersType.INITIAL_DATA))
    }
  }

  return fetchInitialDataAction
}

export default useInitialCatalogData
